import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Checkbox, Chip, InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography, Button, ButtonBase, useMediaQuery } from '@mui/material';
import {
  materialOrderCatalog,
  type MaterialProcurementOrder,
  type MaterialRateItem,
  type MaterialRequirement,
} from '../pages/OfficeEstimationPage';
import NewMaterialDialog from './NewMaterialDialog';
import { orderEntryKey, type NewMaterialInput } from '../office/newMaterial';
import { groupLabel, normVendor, orderGroup, orderName, orderTitle, sameVendorName, vendorSuggestions } from '../office/materialReceiving';
import {
  averageTruckSize, buildingBalances, defaultVendorRate, lowestRate, suggestPoNumbers, toOrderBalance, varietyPicksMissingFromCatalog, vendorRatesFor, vendorUsage, type VendorUse,
  type VendorRate,
} from '../office/orderPlanning';
import {
  BORDER, BORDER_STRONG, Chevron, DARK, FAINT, fmt, FONT_HEADING, GOLD, GOLD_D, GOLD_WASH, GREEN, money, MUTED,
  OutlineButton, PANEL, Pill, PrimaryButton, r3, RED, ScreenBar, ScreenFooter, SectionLabel, Tile, TileText, toNum, WARN, WARN_WASH,
} from './phoneUi';

/** `displayTitle` / `displayGroup` are worked out from the library by the page; without them the saved name is shown. */
export type OrdersScreenOrder = MaterialProcurementOrder & { received: number; pending: number; over: number; displayTitle?: string; displayGroup?: string };
export type OrderDraft = Omit<MaterialProcurementOrder, 'id'>;

const RECENT_LIMIT = 10;
type Step = 'home' | 'detail' | 'unplanned' | 'quotes' | 'order' | 'done';
type Entry = {
  key: string; materialId: string; optionId: string; label: string; /** assembly · variety, without the group */ title: string; unit: string; group: string; item: MaterialRateItem;
  required: number; ordered: number; toOrder: number; rates: VendorRate[];
  /** Vendors this project has already ordered this material from, most relevant first. */
  used: VendorUse[];
  /** Set for a variety listed only in the material's assembly/variety list. */
  assemblyType?: string; variety?: string;
};
type DraftLine = { key: string; qty: number; quoteId: string; vendorName: string; intendedUse: string; split: Array<{ id: string; label: string; qty: number }> | null };

const today = () => new Date().toISOString().slice(0, 10);
const inTwoDays = () => new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10);
const fmtDate = (date?: string) => {
  const value = date ? new Date(`${date}T00:00:00`) : null;
  return value && !Number.isNaN(value.getTime()) ? value.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '';
};
const vendorGroupKey = (name: string) => normVendor(name).replace(/[^a-z0-9]/g, '') || '__none';

function OrderSegmentedAdornment<T extends string>({ value, onChange, options }: {
  value: T; onChange: (value: T) => void; options: Array<{ value: T; label: string }>;
}) {
  return <Stack direction="row" component="span" sx={{ alignSelf: 'center', borderLeft: `1px solid ${BORDER}`, overflow: 'hidden', bgcolor: PANEL, mr: -1.75, my: 0, p: 0, gap: 0, height: 52, borderTopRightRadius: '13px', borderBottomRightRadius: '13px' }}>
    {options.map((option) => {
      const selected = value === option.value;
      return <Box
        component="button"
        type="button"
        key={option.value}
        onClick={() => onChange(option.value)}
        sx={{
          border: 0,
          borderRadius: 0,
          whiteSpace: 'nowrap',
          bgcolor: selected ? GOLD : 'transparent',
          color: selected ? '#fff' : MUTED,
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 900,
          lineHeight: 1.2,
          minWidth: 48,
          px: 1.2,
          py: 0,
          '&:hover': { bgcolor: selected ? GOLD_D : BORDER },
        }}
      >{option.label}</Box>;
    })}
  </Stack>;
}

/** Puts the vendors this project has used for the material first (this variety, then any variety), each newest first. */
function ranked(rates: VendorRate[], used: VendorUse[]): { rates: VendorRate[]; used: VendorUse[] } {
  const position = (rate: VendorRate) => { const index = used.findIndex((entry) => vendorGroupKey(entry.vendor) === vendorGroupKey(rate.vendor)); return index < 0 ? used.length : index; };
  return { rates: [...rates].sort((a, b) => position(a) - position(b) || a.vendor.localeCompare(b.vendor)), used };
}

export default function OrdersScreen({ canManage, canEditPlacedOrders = false, emptyLibraryMessage, knownVendors = [], onCreateMaterial, onAddVariety, onCaptureRate, orders, materialItems, requirements, unplannedCount, unplannedPanel, quoteCount, quotePanel, renderReceipts, onPlaceOrders, onEditOrder, onCloseShort, onReopen }: {
  /** Only purchasing (and admin) can place orders, resolve deliveries, or close order lines. */
  canManage: boolean;
  /** Editing an already placed PO is restricted to admins. */
  canEditPlacedOrders?: boolean;
  /** Shown when there are no materials at all; the page knows why (still loading, or nothing in the shared library). */
  emptyLibraryMessage?: string;
  /** Every vendor in the shared vendor list (all projects). */
  knownVendors?: string[];
  /** Adds a material to the shared library and returns its id. Without it the "add material" option is hidden. */
  onCreateMaterial?: (input: NewMaterialInput) => Promise<string>;
  onAddVariety?: (item: MaterialRateItem, assemblyType: string, variety: string) => Promise<void>;
  /** Optional: when a rate is typed for a line while reviewing the order, saves it to the vendor's quote in the shared library, dated to the order date. */
  onCaptureRate?: (input: { materialId: string; optionId: string; unit: string; materialLabel: string; vendor: string; rate: string; gstMode: 'inclusive' | 'exclusive'; orderDate: string; poNumber: string }) => Promise<void>;
  orders: OrdersScreenOrder[];
  materialItems: MaterialRateItem[];
  requirements: MaterialRequirement[];
  unplannedCount: number;
  unplannedPanel: React.ReactNode;
  quoteCount: number;
  quotePanel: React.ReactNode;
  renderReceipts: (order: OrdersScreenOrder) => React.ReactNode;
  onPlaceOrders: (drafts: OrderDraft[]) => Promise<void>;
  onEditOrder?: (order: OrdersScreenOrder) => void;
  onCloseShort: (order: OrdersScreenOrder) => void;
  onReopen: (order: OrdersScreenOrder) => void;
}) {
  const isDesktop = useMediaQuery('(min-width:900px)');
  const [step, setStep] = useState<Step>('home');
  const [homeFilter, setHomeFilter] = useState<'open' | 'completed'>('open');
  const [homeQuery, setHomeQuery] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [activeDraftKey, setActiveDraftKey] = useState<string | null>(null);
  const [committedKeys, setCommittedKeys] = useState<string[]>([]);
  /** What's typed into a line's quantity field, raw — a truck count or the base unit, depending on that line's mode. */
  const [qtyInputs, setQtyInputs] = useState<Record<string, string>>({});
  /** Ordering a tentative-stock material (sand, aggregate) by the truckload instead of typing the base unit — per line, since several can be entered at once now. */
  const [truckModes, setTruckModes] = useState<Record<string, boolean>>({});
  const [splitOpenKey, setSplitOpenKey] = useState<string | null>(null);
  const [splitDraft, setSplitDraft] = useState<Record<string, string>>({});
  const [vendorInputs, setVendorInputs] = useState<Record<string, string>>({});
  const [poForms, setPoForms] = useState<Record<string, { po: string; date: string }>>({});
  const [error, setError] = useState('');
  const [newMaterialOpen, setNewMaterialOpen] = useState(false);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [priceEntries, setPriceEntries] = useState<Record<string, { rate: string; gstMode: 'inclusive' | 'exclusive'; update?: boolean }>>({});
  const [saving, setSaving] = useState(false);
  const [placed, setPlaced] = useState<Array<{ vendor: string; po: string; date: string }>>([]);
  /** The material search/browse section — open by default, and again on request once at least one
   * material has been added (it auto-collapses after adding, so the person can see what they just added). */
  const [pickerOpen, setPickerOpen] = useState(true);

  // ── everything that can be ordered, with what the estimate still needs ─────
  const entries = useMemo<Entry[]>(() => {
    const date = today();
    const catalog = materialOrderCatalog(materialItems, requirements);
    const fromCatalog = catalog.flatMap((row): Entry[] => {
      const item = materialItems.find((candidate) => candidate.id === row.materialId);
      if (!item) return [];
      const optionId = row.quoteOptionId || '';
      const balance = toOrderBalance(requirements, orders, row.materialId, optionId);
      return [{
        key: row.key, materialId: row.materialId, optionId, label: row.label, title: orderName({ materialId: row.materialId, quoteOptionId: optionId, materialLabel: row.label }, [item]).title, unit: row.unit, group: groupLabel(String(item.group)), item, ...balance,
        ...ranked(vendorRatesFor(item, optionId, row.unit, row.label, date), vendorUsage(orders, row.materialId, optionId)),
      }];
    });
    // Varieties that only exist in a material's assembly/variety list (no quote options) still need to be orderable.
    const fromVarieties = varietyPicksMissingFromCatalog(materialItems, catalog).map((pick): Entry => ({
      key: `variety_${pick.key}`, materialId: pick.item.id, optionId: '', label: pick.label, title: orderName({ materialId: pick.item.id, quoteOptionId: '', materialLabel: pick.label, materialAssemblyType: pick.assemblyType, materialVariety: pick.variety }, [pick.item]).title, unit: pick.unit, group: groupLabel(String(pick.item.group)), item: pick.item,
      required: 0, ordered: 0, toOrder: 0, ...ranked(vendorRatesFor(pick.item, '', pick.unit, pick.label, date), vendorUsage(orders, pick.item.id, '')),
      assemblyType: pick.assemblyType, variety: pick.variety,
    }));
    return [...fromCatalog, ...fromVarieties];
  }, [materialItems, requirements, orders]);
  const entryOf = (key: string) => entries.find((entry) => entry.key === key);
  const vendorList = useMemo(() => {
    const map = new Map<string, { name: string; count: number }>();
    entries.forEach((entry) => entry.rates.forEach((rate) => {
      const key = vendorGroupKey(rate.vendor);
      const current = map.get(key) || { name: rate.vendor, count: 0 };
      current.count += 1;
      map.set(key, current);
    }));
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);

  // ── the order list (was "cart") ─────────────────────────────────────────────
  const lineRate = (line: DraftLine) => entryOf(line.key)?.rates.find((rate) => rate.quoteId === line.quoteId);
  const lineAmount = (line: DraftLine) => (lineRate(line)?.rate || 0) * line.qty;
  const roundRate = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;
  const currentVendorRate = (entry: Entry, line: DraftLine) => entry.rates.find((rate) => sameVendorName(rate.vendor, line.vendorName) && rate.rate > 0);
  const priceGstFactor = (rate: VendorRate | undefined) => 1 + (Number(rate?.gst || 18) || 18) / 100;
  const displayRate = (valueIncl: number, mode: 'inclusive' | 'exclusive', rate: VendorRate | undefined) => roundRate(mode === 'exclusive' ? valueIncl / priceGstFactor(rate) : valueIncl);
  const typedRateIncl = (value: string, mode: 'inclusive' | 'exclusive', rate: VendorRate | undefined) => {
    const entered = toNum(value);
    return mode === 'exclusive' ? entered * priceGstFactor(rate) : entered;
  };
  const effectivePriceUpdate = (line: DraftLine) => priceEntries[line.key]?.update ?? true;
  const vendorGroups = useMemo(() => {
    const groups = new Map<string, { name: string; lines: DraftLine[] }>();
    lines.forEach((line) => {
      const key = vendorGroupKey(line.vendorName);
      const group = groups.get(key) || { name: line.vendorName, lines: [] };
      group.lines.push(line);
      groups.set(key, group);
    });
    return Array.from(groups.entries()).map(([key, group]) => ({ key, ...group }));
  }, [lines]);
  const totalAmount = lines.reduce((sum, line) => sum + lineAmount(line), 0);
  const noRateCount = lines.filter((line) => !(lineRate(line)?.rate)).length;
  const orderVendorName = lines.find((line) => line.vendorName.trim())?.vendorName.trim() || '';

  // The vendor a new line would start with, before it's actually added — used both to add the line
  // and, for a tentative material, to estimate a truck's size from that vendor's own history.
  const predictVendorRate = (entry: Entry) => {
    const usedBefore = entry.used.map((use) => entry.rates.find((rate) => vendorGroupKey(rate.vendor) === vendorGroupKey(use.vendor))).find(Boolean);
    return usedBefore || defaultVendorRate(entry.rates);
  };
  const vendorRateForName = (entry: Entry | undefined, vendor: string) => entry?.rates.find((rate) => sameVendorName(rate.vendor, vendor));
  const addLine = (entry: Entry, qty: number) => {
    setLines((previous) => {
      const existing = previous.find((line) => line.key === entry.key);
      if (existing) return previous.map((line) => line.key === entry.key ? { ...line, qty, split: null } : line);
      const draftVendor = previous.find((line) => line.vendorName.trim())?.vendorName.trim() || '';
      const rate = draftVendor ? vendorRateForName(entry, draftVendor) : predictVendorRate(entry);
      return [...previous, { key: entry.key, qty, quoteId: rate?.quoteId || '', vendorName: draftVendor || rate?.vendor || '', intendedUse: '', split: null }];
    });
  };
  const chooseVendor = (key: string, rate: VendorRate | null, custom?: string) =>
    setLines((previous) => {
      const vendor = rate?.vendor || custom || '';
      return previous.map((line) => {
        const entry = entryOf(line.key);
        const matchingRate = vendorRateForName(entry, vendor);
        return { ...line, vendorName: vendor, quoteId: line.key === key ? rate?.quoteId || matchingRate?.quoteId || '' : matchingRate?.quoteId || '' };
      });
    });
  const setLineIntendedUse = (key: string, intendedUse: string) =>
    setLines((previous) => previous.map((line) => line.key === key ? { ...line, intendedUse } : line));

  /** Adds a material to the order list right where it's picked, defaulting straight to Trucks when
   * that's available — an estimate is still better as the starting point than a plain unit entry the
   * person then has to switch away from. Quantity starts blank; the line still exists so it shows up
   * (with a blank qty field) in the order list below, ready to fill in. */
  const addMaterial = (entry: Entry) => {
    if (lines.some((line) => line.key === entry.key)) {
      setActiveDraftKey(entry.key);
      setPickerOpen(false);
      return;
    }
    addLine(entry, 0);
    setActiveDraftKey(entry.key);
    const vendor = orderVendorName || predictVendorRate(entry)?.vendor || '';
    const estimate = averageTruckSize(orders, entry.materialId, vendor);
    setTruckModes((previous) => ({ ...previous, [entry.key]: entry.item.stockPrecision === 'tentative' && estimate.perTruck > 0 }));
    setQtyInputs((previous) => ({ ...previous, [entry.key]: '' }));
    setPickerOpen(false);
  };
  const removeMaterial = (key: string) => {
    setLines((previous) => previous.filter((line) => line.key !== key));
    setCommittedKeys((previous) => previous.filter((entry) => entry !== key));
    if (activeDraftKey === key) setActiveDraftKey(null);
    setQtyInputs((previous) => { const next = { ...previous }; delete next[key]; return next; });
    if (splitOpenKey === key) setSplitOpenKey(null);
  };
  const commitMaterial = (key: string) => {
    setCommittedKeys((previous) => previous.includes(key) ? previous : [...previous, key]);
    setActiveDraftKey(null);
    setPickerOpen(true);
    setQuery('');
    setError('');
  };
  /** What's typed into a line's quantity field, converting a truck count to the base unit when that
   * line is in Trucks mode. */
  const setLineQtyText = (entry: Entry, text: string) => {
    setQtyInputs((previous) => ({ ...previous, [entry.key]: text }));
    const line = lines.find((candidate) => candidate.key === entry.key);
    const truckMode = truckModes[entry.key] ?? false;
    const raw = toNum(text);
    const qty = truckMode ? r3(raw * averageTruckSize(orders, entry.materialId, line?.vendorName || '').perTruck) : raw;
    addLine(entry, qty);
  };

  // Vendors not already shown as a rate tile: used here before, then quoted on this material or its category, then everyone else.
  const vendorOptionsFor = (entry: Entry): Array<{ name: string; hint: string; group: string }> => {
    const shown = new Set(entry.rates.map((rate) => vendorGroupKey(rate.vendor)));
    const pick = { key: entry.key, item: entry.item, optionId: entry.optionId, assemblyType: entry.assemblyType || '', variety: entry.variety || '', label: entry.label, unit: entry.unit };
    const { related, others } = vendorSuggestions(materialItems, orders, pick, knownVendors);
    const usedHere = entry.used.filter((use) => !shown.has(vendorGroupKey(use.vendor))).map((use) => ({ name: use.vendor, hint: `Used in this project${use.lastPo ? ` · ${use.lastPo}` : ''}`, group: 'Used in this project' }));
    const seen = new Set<string>(Array.from(shown));
    usedHere.forEach((option) => seen.add(vendorGroupKey(option.name)));
    const rest = [
      ...related.map((suggestion) => ({ ...suggestion, group: 'Quoted for this material or category' })),
      ...others.map((suggestion) => ({ ...suggestion, group: 'All vendors' })),
    ].filter((option) => !seen.has(vendorGroupKey(option.name)));
    return [...usedHere, ...rest];
  };

  /**
   * The vendor for one order line: every vendor with a quote on this material, its rate right there
   * for comparison, plus a search for any other vendor. Always shown expanded — with the rate visible
   * per vendor, there's no reason to hide it behind a collapsed chip first.
   */
  const renderVendorPicker = (entry: Entry, line: DraftLine, onPick: (rate: VendorRate | null, name?: string) => void = (rate, name) => chooseVendor(entry.key, rate, name)): React.ReactNode => {
    const pick = (rate: VendorRate | null, name?: string) => onPick(rate, name);
    const lowest = lowestRate(entry.rates);
    const otherVendorOptions = vendorOptionsFor(entry);
    const typed = vendorInputs[line.key] || '';
    // Typing a name not in the list and pressing Enter (or leaving the field) applies it directly —
    // there's no separate confirm step, matching how picking a vendor tile above already applies at once.
    const applyTyped = () => {
      const value = typed.trim();
      if (!value) return;
      const known = otherVendorOptions.find((option) => sameVendorName(option.name, value));
      pick(null, known ? known.name : value);
    };
    return <Stack gap={0.9}>
      <Autocomplete
        freeSolo
        fullWidth
        options={otherVendorOptions}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
        filterOptions={(options, state) => { const text = vendorGroupKey(state.inputValue); return options.filter((option) => !text || vendorGroupKey(option.name).includes(text)); }}
        inputValue={typed}
        onInputChange={(_, value) => setVendorInputs((previous) => ({ ...previous, [line.key]: value }))}
        onChange={(_, value) => { if (value && typeof value !== 'string') pick(null, value.name); }}
        onBlur={applyTyped}
        renderOption={(props, option) => <li {...props} key={option.name}><Box><Typography sx={{ fontWeight: 800 }}>{option.name}</Typography>{option.hint && <Typography sx={{ fontSize: 12, color: MUTED }}>{option.hint}</Typography>}</Box></li>}
        renderInput={(params) => <TextField {...params} placeholder="Search or add a vendor" onKeyDown={(event) => { if (event.key === 'Enter') applyTyped(); }} inputProps={{ ...params.inputProps, 'aria-label': 'Other vendor' }} InputProps={{ ...params.InputProps, sx: { borderRadius: '14px', bgcolor: '#fff', minHeight: 54, fontSize: 17 } }} />}
      />
      <Stack direction="row" gap={0.75} flexWrap="wrap">
        {entry.rates.map((rate) => {
          const selected = rate.quoteId === line.quoteId;
          const low = !selected && lowest && lowest.quoteId === rate.quoteId && entry.rates.filter((r) => r.rate > 0).length > 1;
          return <Chip
            key={rate.quoteId}
            label={rate.vendor}
            onClick={() => pick(rate)}
            variant={selected ? 'filled' : 'outlined'}
            sx={{
              height: 38,
              borderRadius: '19px',
              fontSize: 14,
              fontWeight: selected ? 700 : 500,
              bgcolor: selected ? GOLD_WASH : 'transparent',
              borderColor: selected ? GOLD : low ? GREEN : GOLD,
              color: selected ? GOLD_D : low ? GREEN : GOLD_D,
              '& .MuiChip-label': { px: 1.2 },
            }}
          />;
        })}
      </Stack>
      {!entry.rates.length && <Typography sx={{ color: MUTED, fontSize: 13 }}>No vendor has a quote for this material yet.</Typography>}
    </Stack>;
  };

  // ── search and lists ────────────────────────────────────────────────────────
  // Lists start with what was used recently; searching the whole library needs at least 3 letters.
  const typed = query.trim();
  const needle = typed.length >= 3 ? typed.toLowerCase() : '';
  const tooShort = typed.length > 0 && typed.length < 3;
  const recentEntries = useMemo(() => {
    const seen = new Set<string>();
    const found: Entry[] = [];
    [...orders].filter((order) => order.materialId && !order.unplannedRejected)
      .sort((a, b) => String(b.orderDate || '').localeCompare(String(a.orderDate || '')))
      .forEach((order) => {
        if (found.length >= RECENT_LIMIT) return;
        const entry = entries.find((candidate) => candidate.materialId === order.materialId && candidate.optionId === (order.quoteOptionId || '') && (candidate.variety || '') === (order.materialVariety || ''));
        if (entry && !seen.has(entry.key)) { seen.add(entry.key); found.push(entry); }
      });
    return found;
  }, [orders, entries]);
  const matching = (list: Entry[]) => list.filter((entry) => !needle || `${entry.label} ${entry.group}`.toLowerCase().includes(needle));
  const materialTile = (entry: Entry) => {
    const line = lines.find((candidate) => candidate.key === entry.key);
    const sub = <>
      {entry.group}{entry.required <= 0 && !entry.rates.length ? ' · no vendor quotes yet' : ''}
    </>;
    return <Tile key={entry.key} selected={!!line} onClick={() => addMaterial(entry)}>
      <TileText title={entry.title} sub={sub} />
      {line ? (line.qty > 0 ? <Pill tone="ok">{fmt(line.qty)} {entry.unit}</Pill> : <Pill tone="warn">Added</Pill>) : <Pill>+ Add</Pill>}
    </Tile>;
  };

  const goNew = () => { setQuery(''); setError(''); setPendingKey(null); setPickerOpen(true); setStep('order'); };
  // A material just added to the library reaches the list a moment later; add it to the order as soon as it does.
  useEffect(() => {
    if (!pendingKey) return;
    const entry = entries.find((candidate) => candidate.key === pendingKey);
    if (!entry) return;
    setPendingKey(null); setNewMaterialOpen(false); addMaterial(entry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKey, entries]);
  const createdMaterial = async (input: NewMaterialInput) => {
    if (!onCreateMaterial) return;
    const id = await onCreateMaterial(input);
    setQuery(input.materialName); setPendingKey(orderEntryKey(id, input.materialName, input.assemblyType, input.variety));
  };
  const addedVariety = async (item: MaterialRateItem, assemblyType: string, variety: string) => {
    if (!onAddVariety) return;
    await onAddVariety(item, assemblyType, variety);
    setQuery(item.materialName); setPendingKey(orderEntryKey(item.id, item.materialName, assemblyType, variety));
  };
  // An existing material: show it in the list, where its kinds can be picked.
  const useExisting = (item: MaterialRateItem) => { setNewMaterialOpen(false); setQuery(item.materialName); setStep('order'); };
  // A vendor group appears here as soon as its first line does — keep a PO/date form ready for it
  // without wiping what's already been typed for groups that were already there.
  useEffect(() => {
    setPoForms((previous) => {
      const missing = vendorGroups.filter((group) => !previous[group.key]);
      if (!missing.length) return previous;
      const usedPoNumbers = [...orders.map((order) => String(order.poNumber || '')), ...Object.values(previous).map((form) => form.po)];
      const numbers = suggestPoNumbers(usedPoNumbers, missing.length);
      return { ...previous, ...Object.fromEntries(missing.map((group, index) => [group.key, { po: numbers[index], date: inTwoDays() }])) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorGroups]);
  const place = async () => {
    const activeLines = lines.filter((line) => line.qty > 0);
    if (!activeLines.length) { setError('Enter a quantity for at least one material.'); return; }
    const missing = activeLines.find((line) => !line.vendorName.trim());
    if (missing) { setError(`Choose a vendor for ${entryOf(missing.key)?.label || 'every line'} first.`); return; }
    const activeVendors = Array.from(new Set(activeLines.map((line) => vendorGroupKey(line.vendorName))));
    if (activeVendors.length > 1) { setError('Place separate orders for separate vendors.'); return; }
    const activeGroups = vendorGroups.filter((group) => group.lines.some((line) => line.qty > 0));
    const used = new Set<string>();
    for (const group of activeGroups) {
      const form = poForms[group.key];
      const po = form?.po.trim() || '';
      if (!po) { setError('Every PO needs a number.'); return; }
      if (used.has(po.toLowerCase())) { setError(`${po} is used twice. Give each vendor its own PO number.`); return; }
      used.add(po.toLowerCase());
      const clash = orders.find((order) => String(order.poNumber || '').trim().toLowerCase() === po.toLowerCase() && vendorGroupKey(order.vendor || '') !== group.key);
      if (clash) { setError(`${po} is already used by ${clash.vendor || 'another vendor'}.`); return; }
    }
    const drafts: OrderDraft[] = activeLines.flatMap((line) => {
      const entry = entryOf(line.key);
      if (!entry) return [];
      const rate = lineRate(line);
      const form = poForms[vendorGroupKey(line.vendorName)];
      const split = line.split && line.split.length ? line.split : null;
      return [{
        materialId: entry.materialId,
        quoteOptionId: entry.optionId,
        ...(entry.assemblyType ? { materialAssemblyType: entry.assemblyType } : {}),
        ...(entry.variety ? { materialVariety: entry.variety } : {}),
        materialLabel: entry.label,
        unit: entry.unit,
        vendor: line.vendorName,
        brand: rate?.brand || entry.item.brand || '',
        code: rate?.code || '',
        quoteId: rate?.quoteId || '',
        ...(rate && rate.snapshot > 0 ? { rateSnapshot: rate.snapshot, rateUnit: rate.rateUnit, rateEffectiveFrom: rate.effectiveFrom } : {}),
        scopeType: split ? 'building' as const : 'project' as const,
        scopeId: '',
        scopeLabel: '',
        orderQty: line.qty,
        orderDate: today(),
        expectedDate: form.date,
        poNumber: form.po.trim(),
        intendedUse: line.intendedUse.trim(),
        buildingAllocations: split ? split.map((part) => ({ buildingId: part.id, buildingLabel: part.label, qty: part.qty })) : [],
        receipts: [],
      }];
    });
    setSaving(true); setError('');
    try {
      await onPlaceOrders(drafts);
      if (onCaptureRate) {
        const orderDate = today();
        await Promise.allSettled(activeLines.flatMap((line) => {
          const priced = priceEntries[line.key];
          const entry = entryOf(line.key);
          if (!priced?.rate.trim() || !entry || !effectivePriceUpdate(line)) return [];
          return [onCaptureRate({ materialId: entry.materialId, optionId: entry.optionId, unit: entry.unit, materialLabel: entry.label, vendor: line.vendorName, rate: priced.rate, gstMode: priced.gstMode, orderDate, poNumber: poForms[vendorGroupKey(line.vendorName)]?.po.trim() || '' })];
        }));
      }
      setPlaced(activeGroups.map((group) => ({ vendor: group.name, po: poForms[group.key].po.trim(), date: poForms[group.key].date })));
      setLines([]); setCommittedKeys([]); setActiveDraftKey(null); setPriceEntries({}); setQtyInputs({}); setTruckModes({}); setSplitOpenKey(null); setPoForms({}); setStep('done');
    } catch (err: any) {
      setError(err?.message || 'Could not place the orders.');
    } finally { setSaving(false); }
  };

  const back = () => {
    setError('');
    setStep('home');
  };

  // ── screens ─────────────────────────────────────────────────────────────────
  const renderScreen = (screen: Step, embedded: boolean): React.ReactNode => {
  let content: React.ReactNode;

  if (screen === 'home') {
    const text = homeQuery.trim().toLowerCase();
    const visible = orders
      .filter((order) => !order.unplannedRejected && (homeFilter === 'open' ? order.pending > 0 : order.pending === 0))
      .filter((order) => !text || `${order.materialLabel} ${order.vendor || ''} ${order.poNumber || ''}`.toLowerCase().includes(text))
      .sort((a, b) => String(b.orderDate || '').localeCompare(String(a.orderDate || '')))
      .slice(0, homeFilter === 'completed' ? 40 : 500);
    const orderSub = (order: OrdersScreenOrder, showVendor: boolean) => <>{orderGroup(order) && <>{orderGroup(order)}<br /></>}{`${showVendor ? `${order.vendor?.trim() || 'Vendor not set'} · ` : ''}${order.poNumber ? `${order.poNumber} · ` : ''}${homeFilter === 'open' ? `pending ${fmt(order.pending)} ${order.unit}` : `${fmt(order.received)} ${order.unit} received`}${order.expectedDate && homeFilter === 'open' ? ` · due ${fmtDate(order.expectedDate)}` : ''}`}</>;
    const orderTile = (order: OrdersScreenOrder, showVendor: boolean) => <Tile key={order.id} onClick={() => { setDetailId(order.id); setStep('detail'); }}>
      <TileText title={orderTitle(order)} sub={orderSub(order, showVendor)} />
      <Chevron />
    </Tile>;
    // A vendor's open orders are shown as a plain list under its own header bar, not as individual cards.
    const orderRow = (order: OrdersScreenOrder) => <ButtonBase key={order.id} onClick={() => { setDetailId(order.id); setStep('detail'); }} sx={{ width: '100%', display: 'flex', textAlign: 'left', px: 1.5, py: 1, borderTop: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
      <TileText title={orderTitle(order)} sub={orderSub(order, false)} />
    </ButtonBase>;
    const groups = new Map<string, { name: string; list: OrdersScreenOrder[] }>();
    visible.forEach((order) => { const key = vendorGroupKey(order.vendor || ''); const group = groups.get(key) || { name: order.vendor?.trim() || 'Vendor not set', list: [] }; group.list.push(order); groups.set(key, group); });
    content = <>

      <Stack gap={1.25} sx={{ flex: 1, p: { xs: 0, md: 1.75 } }}>
        {canManage && <>
          <PrimaryButton onClick={() => goNew()} fullWidth>+ New order</PrimaryButton>
          <SectionLabel>Needs your attention</SectionLabel>
          {unplannedCount > 0 && <Tile attention onClick={() => setStep('unplanned')}><TileText title={`${unplannedCount} deliver${unplannedCount === 1 ? 'y' : 'ies'} without a PO`} sub="Confirm, link or send back" /><Chevron /></Tile>}
          {quoteCount > 0 && <Tile attention onClick={() => setStep('quotes')}><TileText title={`${quoteCount} vendor${quoteCount === 1 ? '' : 's'} need a rate quote`} sub="Delivered but no quote yet" /><Chevron /></Tile>}
        </>}
        <SectionLabel>Orders</SectionLabel>
        <TextField value={homeQuery} onChange={(event) => setHomeQuery(event.target.value)} placeholder="Search material, vendor or PO" inputProps={{ 'aria-label': 'Search orders' }} InputProps={{ sx: { borderRadius: '15px', bgcolor: PANEL } }} fullWidth />
        <ToggleButtonGroup exclusive fullWidth value={homeFilter} onChange={(_, value) => value && setHomeFilter(value)} aria-label="Order status" sx={segSx}>
          <ToggleButton value="open">Open</ToggleButton><ToggleButton value="completed">Completed</ToggleButton>
        </ToggleButtonGroup>
        {homeFilter === 'open'
          ? <Stack gap={1.25}>
            {Array.from(groups.values()).map((group) => <Box key={group.name} sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, overflow: 'hidden' }}>
              <Stack direction="row" alignItems="center" gap={1} sx={{ px: 1.5, py: 0.9, bgcolor: PANEL }}>
                <Typography sx={{ flex: 1, fontWeight: 700 }}>{group.name}</Typography>
                <Pill tone="warn">{group.list.length} open</Pill>
              </Stack>
              {group.list.map((order) => orderRow(order))}
            </Box>)}
          </Stack>
          : visible.map((order) => orderTile(order, true))}
        {!visible.length && <Typography sx={{ color: MUTED, fontSize: 13 }}>{homeFilter === 'open' ? 'No open orders.' : 'No completed orders yet.'}</Typography>}
      </Stack>
    </>;
  } else if (screen === 'detail') {
    const order = orders.find((candidate) => candidate.id === detailId);
    const canEditThisOrder = canEditPlacedOrders;
    content = !order ? <><ScreenBar title="Order" onBack={back} /><Typography sx={{ p: 2, color: MUTED }}>This order no longer exists.</Typography></> : <>
      <ScreenBar title={orderTitle(order)} sub={orderGroup(order)} onBack={back} />
      <Stack gap={1.5} sx={{ flex: 1, p: 1.75 }}>
        <Typography sx={{ color: MUTED, fontSize: 13 }}>{order.vendor?.trim() || 'Vendor not set'}{order.poNumber ? ` · ${order.poNumber}` : ''}{order.expectedDate ? ` · due ${fmtDate(order.expectedDate)}` : ''}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
          {[['Ordered', `${fmt(order.orderQty)} ${order.unit}`, DARK], ['Received', `${fmt(order.received)} ${order.unit}`, order.over ? WARN : GREEN], [order.closedShort ? 'Closed short' : 'Pending', `${fmt(order.closedShort ? Math.max(0, order.orderQty - order.received) : order.pending)} ${order.unit}`, order.pending ? WARN : MUTED]].map(([label, value, color]) => <Box key={label}><Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: FAINT }}>{label}</Typography><Typography sx={{ fontWeight: 600, color }}>{value}</Typography></Box>)}
        </Box>
        {order.over > 0 && <Typography sx={{ color: WARN, fontSize: 12.5, fontWeight: 600 }}>+{fmt(order.over)} {order.unit} more than ordered</Typography>}
        <SectionLabel>Receipts</SectionLabel>
        {renderReceipts(order)}
      </Stack>
      {(canEditThisOrder || (canManage && (order.pending > 0 || order.closedShort))) && <ScreenFooter>
        {canEditThisOrder && <OutlineButton fullWidth onClick={() => onEditOrder?.(order)}>Edit order</OutlineButton>}
        {canManage && (order.pending > 0 || order.closedShort) && (order.closedShort
          ? <OutlineButton fullWidth onClick={() => onReopen(order)}>Reopen</OutlineButton>
          : <OutlineButton fullWidth onClick={() => onCloseShort(order)} sx={{ color: RED, borderColor: RED }}>{order.received > 0 ? 'Close short' : 'Cancel order'}</OutlineButton>)}
      </ScreenFooter>}
    </>;
  } else if (screen === 'unplanned') {
    content = <><ScreenBar title="Deliveries without a PO" onBack={back} /><Box sx={{ flex: 1, p: 1.75 }}>{unplannedPanel || <Typography sx={{ color: MUTED }}>All resolved.</Typography>}</Box></>;
  } else if (screen === 'quotes') {
    content = <><ScreenBar title="Vendors waiting for a quote" onBack={back} /><Box sx={{ flex: 1, p: 1.75 }}>{quotePanel || <Typography sx={{ color: MUTED }}>All done.</Typography>}</Box></>;
  } else if (screen === 'order') {
    const list = needle ? matching(entries) : recentEntries;
    const showRecentMaterials = !needle;
    const byGroup = new Map<string, Entry[]>();
    list.forEach((entry) => byGroup.set(entry.group, [...(byGroup.get(entry.group) || []), entry]));
    const showPicker = pickerOpen || !lines.length;
    content = <>
      <ScreenBar
        title={embedded ? 'Add materials' : 'New order'}
        onBack={embedded ? undefined : back}
      />
      <Stack gap={1.5} sx={{ flex: 1, p: 1.75 }}>
        {showPicker && !lines.length ? <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '15px', p: 1.25, bgcolor: PANEL }}>
          <Stack gap={1.25}>
            <TextField value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search material" inputProps={{ 'aria-label': 'Search material' }} InputProps={{ sx: { borderRadius: '15px', fontSize: 16, bgcolor: '#fff' } }} fullWidth />
            {tooShort && <Typography sx={{ color: MUTED, fontSize: 13 }}>Type at least 3 letters to search.</Typography>}
            {showRecentMaterials && list.length > 0 && <SectionLabel>Recently ordered</SectionLabel>}
            {showRecentMaterials
              ? list.map(materialTile)
              : Array.from(byGroup.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([group, list2]) => <Stack key={group} gap={0.75}>
                <SectionLabel>{group}</SectionLabel>
                {list2.map(materialTile)}
              </Stack>)}
            {showRecentMaterials && list.length > 0 && <Typography sx={{ color: MUTED, fontSize: 12.5 }}>Type at least 3 letters to find any other material.</Typography>}
            {!list.length && <Typography sx={{ color: MUTED, fontSize: 13 }}>{!entries.length ? (emptyLibraryMessage || 'The rate library has no materials for this project yet. Add them in Estimation > Rates.') : needle ? `No material matches “${typed}”.` : 'Nothing ordered in this project yet. Type at least 3 letters to search all materials.'}</Typography>}
            {canManage && onCreateMaterial && <Button onClick={() => setNewMaterialOpen(true)} sx={{ alignSelf: 'flex-start', color: GOLD_D, fontWeight: 600, textTransform: 'none' }}>{needle ? `Not listed? Add “${typed}” as a new material` : '+ Add a new material'}</Button>}
            {!!lines.length && <Button onClick={() => setPickerOpen(false)} sx={{ alignSelf: 'flex-start', color: MUTED, textTransform: 'none' }}>Done adding materials</Button>}
          </Stack>
        </Box> : null}

        <SectionLabel>Order lines</SectionLabel>
        {!lines.length && <Typography sx={{ color: MUTED, fontSize: 13 }}>Pick a material above to add it here.</Typography>}
        {vendorGroups.map((group) => {
          const subtotal = group.lines.reduce((sum, line) => sum + lineAmount(line), 0);
          const form = poForms[group.key] || { po: '', date: inTwoDays() };
          const vendorLine = group.lines[0];
          const vendorEntry = vendorLine ? entryOf(vendorLine.key) : undefined;
          return <Box key={group.key} sx={{ border: `1px solid ${BORDER}`, borderRadius: '20px', overflow: 'hidden', bgcolor: '#fff', boxShadow: '0 1px 0 rgba(0,0,0,.02)' }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ px: { xs: 1.4, sm: 1.7 }, py: 1.15, bgcolor: PANEL, borderBottom: `1px solid ${BORDER}` }}>
              <Typography sx={{ flex: 1, fontWeight: 700, fontSize: 17, color: DARK }}>{group.name || 'No vendor chosen'}</Typography>
              <Typography sx={{ color: MUTED, fontSize: 13, fontWeight: 700 }}>{subtotal ? money(subtotal) : ''}</Typography>
            </Stack>
            <Box sx={{ px: { xs: 1.4, sm: 1.7 }, py: 1.15, bgcolor: '#fffaf0', borderBottom: `1px solid ${BORDER}` }}>
              {vendorEntry && vendorLine && <Box sx={{ mb: 1 }}>
                {renderVendorPicker(vendorEntry, vendorLine)}
              </Box>}
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                <TextField
                  label="PO number"
                  value={form.po}
                  onChange={(event) => { setPoForms((previous) => ({ ...previous, [group.key]: { ...form, po: event.target.value } })); setError(''); }}
                  fullWidth
                  sx={{ '& .MuiInputBase-root': { borderRadius: '13px', bgcolor: '#fff' } }}
                />
                <TextField
                  label="Expected delivery"
                  type="date"
                  value={form.date}
                  onChange={(event) => setPoForms((previous) => ({ ...previous, [group.key]: { ...form, date: event.target.value } }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{ '& .MuiInputBase-root': { borderRadius: '13px', bgcolor: '#fff' } }}
                />
              </Stack>
            </Box>
            {group.lines.map((line) => {
              const entry = entryOf(line.key);
              if (!entry) return null;
              const rate = lineRate(line);
              const savedCompact = committedKeys.includes(line.key) && activeDraftKey !== line.key;
              if (savedCompact) return <Stack key={line.key} direction="row" alignItems="center" gap={1} sx={{ px: { xs: 1.4, sm: 1.7 }, py: 1.15, borderTop: `1px solid ${BORDER}` }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 15.5, color: DARK, overflowWrap: 'anywhere' }}>{entry.title}</Typography>
                  <Typography sx={{ mt: 0.15, fontSize: 12.5, color: MUTED }}>
                    {fmt(line.qty)} {entry.unit}{line.intendedUse.trim() ? ` · ${line.intendedUse.trim()}` : ''}{rate?.rate ? ` · ${money(lineAmount(line))}` : ''}
                  </Typography>
                </Box>
                <Button size="small" onClick={() => { setActiveDraftKey(line.key); setPickerOpen(false); }} sx={{ color: GOLD_D, textTransform: 'none', fontWeight: 700 }}>Edit</Button>
                <Button size="small" onClick={() => removeMaterial(line.key)} sx={{ minWidth: 0, color: RED, textTransform: 'none' }}>Remove</Button>
              </Stack>;
              const price = priceEntries[line.key];
              const currentRate = currentVendorRate(entry, line);
              const priceMode = price?.gstMode || 'inclusive';
              const gstLabel = priceMode === 'exclusive' ? 'excl GST' : 'incl GST';
              const enteredDisplay = toNum(price?.rate || '');
              const currentDisplay = currentRate ? displayRate(currentRate.rate, priceMode, currentRate) : 0;
              const enteredIncl = typedRateIncl(price?.rate || '', priceMode, currentRate);
              const differsFromLibrary = !!price?.rate.trim() && !!currentRate && Math.abs(enteredIncl - currentRate.rate) > Math.max(0.01, currentRate.rate * 0.005);
              const lowest = lowestRate(entry.rates);
              const cheaper = lowest && rate && rate.rate > 0 && lowest.quoteId !== rate.quoteId && lowest.rate < rate.rate ? lowest : null;
              const hasBuildings = buildingBalances(requirements, orders, entry.materialId, entry.optionId).length > 0;
              const truckMode = truckModes[line.key] ?? false;
              const truckEstimate = averageTruckSize(orders, entry.materialId, line.vendorName);
              const canUseTrucks = entry.item.stockPrecision === 'tentative' && truckEstimate.perTruck > 0;
              const qtyText = qtyInputs[line.key] ?? '';
              const balances = buildingBalances(requirements, orders, entry.materialId, entry.optionId);
              const splitTotal = balances.reduce((sum, building) => sum + toNum(splitDraft[building.id]), 0);
              const rateFieldValue = price?.rate !== undefined ? price.rate : (currentRate ? String(currentDisplay) : '');
              return <Stack key={line.key} gap={1.05} sx={{ px: { xs: 1.4, sm: 1.7 }, py: 1.35, borderTop: `1px solid ${BORDER}` }}>
                <Stack direction="row" alignItems="flex-start" gap={1.25}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 17, sm: 18 }, color: DARK, lineHeight: 1.2, overflowWrap: 'anywhere' }}>{entry.title}</Typography>
                    <Typography sx={{ mt: 0.25, fontSize: 14, color: MUTED }}>{entry.group}</Typography>
                  </Box>
                  <ButtonBase aria-label={`Remove ${entry.title}`} onClick={() => removeMaterial(line.key)} sx={{ width: 42, height: 42, flex: '0 0 auto', borderRadius: '50%', border: `1px solid ${BORDER_STRONG}`, color: MUTED, fontSize: 22, bgcolor: '#fff', '&:hover': { bgcolor: PANEL, color: DARK } }}>×</ButtonBase>
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: onCaptureRate && line.vendorName ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)' }, gap: 1, alignItems: 'start' }}>
                  <Stack gap={0.55}>
                    <TextField
                      label={truckMode ? 'Trucks' : `Quantity (${entry.unit})`} value={qtyText}
                      onChange={(event) => setLineQtyText(entry, event.target.value)}
                      inputProps={{ inputMode: 'decimal', 'aria-label': `Quantity ${entry.title}` }}
                      sx={{ width: '100%', maxWidth: '100%', '& .MuiInputBase-root': { borderRadius: '13px', minHeight: 52, fontSize: 17, bgcolor: '#fff' } }}
                      InputProps={canUseTrucks ? {
                        endAdornment: <InputAdornment position="end" sx={{ m: 0, height: '100%', maxHeight: 'none' }}>
                          <OrderSegmentedAdornment
                            value={truckMode ? 'trucks' : 'unit'}
                            onChange={(next) => {
                              setTruckModes((previous) => ({ ...previous, [line.key]: next === 'trucks' }));
                              setQtyInputs((previous) => ({ ...previous, [line.key]: '' }));
                              addLine(entry, 0);
                            }}
                            options={[{ value: 'unit', label: entry.unit }, { value: 'trucks', label: 'Trucks' }]}
                          />
                        </InputAdornment>,
                      } : undefined}
                    />
                    {truckMode && toNum(qtyText) > 0 && <Typography sx={{ color: GOLD_D, fontSize: 12.5, fontWeight: 600 }}>≈ {fmt(line.qty)} {entry.unit}</Typography>}
                  </Stack>
                  {onCaptureRate && line.vendorName && <Stack gap={0.55}>
                    <TextField
                      label={`Rate (${entry.unit})`} value={rateFieldValue}
                      onChange={(event) => setPriceEntries((previous) => ({ ...previous, [line.key]: { rate: event.target.value, gstMode: previous[line.key]?.gstMode || 'inclusive', update: previous[line.key]?.update } }))}
                      inputProps={{ inputMode: 'decimal', 'aria-label': `Rate for ${entry.title}` }}
                      sx={{ width: '100%', maxWidth: '100%', '& .MuiInputBase-root': { borderRadius: '13px', minHeight: 52, fontSize: 17, bgcolor: '#fff' } }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end" sx={{ m: 0, height: '100%', maxHeight: 'none' }}>
                          <OrderSegmentedAdornment
                            value={priceMode}
                            onChange={(value) => setPriceEntries((previous) => ({ ...previous, [line.key]: { rate: previous[line.key]?.rate ?? rateFieldValue, gstMode: value, update: previous[line.key]?.update } }))}
                            options={[{ value: 'inclusive', label: 'Incl' }, { value: 'exclusive', label: 'Excl' }]}
                          />
                        </InputAdornment>,
                      }}
                    />
                  </Stack>}
                </Box>
                <TextField
                  label="Use / location"
                  placeholder="Building, trimix work, plumber use, flooring work..."
                  value={line.intendedUse}
                  onChange={(event) => setLineIntendedUse(line.key, event.target.value)}
                  inputProps={{ 'aria-label': `Use or location for ${entry.title}` }}
                  sx={{ width: '100%', '& .MuiInputBase-root': { borderRadius: '13px', minHeight: 50, fontSize: 15.5, bgcolor: '#fff' } }}
                />
                {cheaper && <Typography sx={{ fontSize: 12.5, color: WARN }}>{cheaper.vendor} is {money((rate!.rate - cheaper.rate) * line.qty)} cheaper for this quantity</Typography>}
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                  {hasBuildings && !line.split && <Button size="small" onClick={() => { setSplitOpenKey(splitOpenKey === line.key ? null : line.key); setSplitDraft({}); setError(''); }} sx={{ color: GOLD_D, textTransform: 'none' }}>{splitOpenKey === line.key ? 'Cancel split' : 'Split by building'}</Button>}
                  {line.split && <><Pill tone="info">{line.split.map((part) => `${part.label}: ${fmt(part.qty)}`).join(' · ')}</Pill><Button size="small" onClick={() => setLines((previous) => previous.map((candidate) => candidate.key === line.key ? { ...candidate, split: null } : candidate))} sx={{ color: MUTED, textTransform: 'none' }}>Clear split</Button></>}
                </Stack>
                {splitOpenKey === line.key && <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '15px', p: 1.25, bgcolor: PANEL }}>
                  <Typography sx={{ color: MUTED, fontSize: 12.5, mb: 0.75 }}>Ordering {fmt(line.qty)} {entry.unit}. Enter how much goes to each building.</Typography>
                  <Stack gap={1}>
                    {balances.map((building) => <TextField key={building.id} size="small" label={`${building.label} · balance ${fmt(building.balance)}`} value={splitDraft[building.id] ?? ''} onChange={(event) => { setSplitDraft((previous) => ({ ...previous, [building.id]: event.target.value })); setError(''); }} inputProps={{ inputMode: 'decimal' }} fullWidth />)}
                    <Typography sx={{ fontSize: 12.5, color: r3(splitTotal) === line.qty ? GREEN : WARN }}>Allocated {fmt(r3(splitTotal))} of {fmt(line.qty)} {entry.unit}</Typography>
                    <Button size="small" variant="contained" onClick={() => {
                      if (r3(splitTotal) !== line.qty) { setError(`The split must add up to ${fmt(line.qty)}.`); return; }
                      const parts = balances.filter((building) => toNum(splitDraft[building.id]) > 0).map((building) => ({ id: building.id, label: building.label, qty: toNum(splitDraft[building.id]) }));
                      setLines((previous) => previous.map((candidate) => candidate.key === line.key ? { ...candidate, split: parts } : candidate));
                      setError(''); setSplitOpenKey(null);
                    }} sx={{ alignSelf: 'flex-start', bgcolor: GOLD, '&:hover': { bgcolor: GOLD_D } }}>Save split</Button>
                  </Stack>
                </Box>}
                {onCaptureRate && line.vendorName && <Stack gap={0.75}>
                  {differsFromLibrary && <Box sx={{ p: 1, borderRadius: '15px', bgcolor: WARN_WASH, border: `1px solid ${WARN}` }}>
                    <Typography sx={{ color: WARN, fontSize: 12.5, fontWeight: 900 }}>Rate differs from library: ₹{fmt(enteredDisplay)} vs ₹{fmt(currentDisplay)} {gstLabel}</Typography>
                  </Box>}
                  {price?.rate.trim() && <Stack direction="row" alignItems="center">
                    <Checkbox size="small" checked={effectivePriceUpdate(line)} onChange={(event) => setPriceEntries((previous) => ({ ...previous, [line.key]: { rate: previous[line.key]?.rate || '', gstMode: previous[line.key]?.gstMode || 'inclusive', update: event.target.checked } }))} sx={{ p: 0.5 }} inputProps={{ 'aria-label': `Update the rate library for ${entry.title}` }} />
                    <Typography sx={{ fontSize: 11.5, color: MUTED }}>Update {line.vendorName}'s rate in the shared library, dated today.</Typography>
                  </Stack>}
                </Stack>}
                <Button
                  variant="outlined"
                  disabled={!line.vendorName.trim() || line.qty <= 0}
                  onClick={() => commitMaterial(line.key)}
                  sx={{ minHeight: 46, borderRadius: '15px', borderColor: BORDER_STRONG, color: GOLD_D, fontWeight: 800, textTransform: 'none', '&:hover': { borderColor: GOLD, bgcolor: '#fffaf0' } }}
                >
                  {committedKeys.includes(line.key) ? 'Done' : '+ Add material to order'}
                </Button>
              </Stack>;
            })}
          </Box>;
        })}
        {!!lines.length && showPicker ? <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '15px', p: 1.25, bgcolor: PANEL }}>
          <Stack gap={1.25}>
            <TextField value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search material" inputProps={{ 'aria-label': 'Search material' }} InputProps={{ sx: { borderRadius: '15px', fontSize: 16, bgcolor: '#fff' } }} fullWidth />
            {tooShort && <Typography sx={{ color: MUTED, fontSize: 13 }}>Type at least 3 letters to search.</Typography>}
            {showRecentMaterials && list.length > 0 && <SectionLabel>Recently ordered</SectionLabel>}
            {showRecentMaterials
              ? list.map(materialTile)
              : Array.from(byGroup.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([group, list2]) => <Stack key={group} gap={0.75}>
                <SectionLabel>{group}</SectionLabel>
                {list2.map(materialTile)}
              </Stack>)}
            {showRecentMaterials && list.length > 0 && <Typography sx={{ color: MUTED, fontSize: 12.5 }}>Type at least 3 letters to find any other material.</Typography>}
            {!list.length && <Typography sx={{ color: MUTED, fontSize: 13 }}>{!entries.length ? (emptyLibraryMessage || 'The rate library has no materials for this project yet. Add them in Estimation > Rates.') : needle ? `No material matches “${typed}”.` : 'Nothing ordered in this project yet. Type at least 3 letters to search all materials.'}</Typography>}
            {canManage && onCreateMaterial && <Button onClick={() => setNewMaterialOpen(true)} sx={{ alignSelf: 'flex-start', color: GOLD_D, fontWeight: 600, textTransform: 'none' }}>{needle ? `Not listed? Add “${typed}” as a new material` : '+ Add a new material'}</Button>}
            <Button onClick={() => setPickerOpen(false)} sx={{ alignSelf: 'flex-start', color: MUTED, textTransform: 'none' }}>Done adding materials</Button>
          </Stack>
        </Box> : null}
        {!!lines.length && !showPicker && !activeDraftKey && <Button
          fullWidth
          variant="outlined"
          onClick={() => setPickerOpen(true)}
          sx={{ minHeight: 52, borderRadius: '15px', borderColor: BORDER_STRONG, color: GOLD_D, bgcolor: '#fff', fontWeight: 800, textTransform: 'none', '&:hover': { borderColor: GOLD, bgcolor: '#fffaf0' } }}
        >
          + Add Material
        </Button>}
        {error && <Typography sx={{ color: RED, fontSize: 13 }}>{error}</Typography>}
      </Stack>
      <ScreenFooter>
        <Typography sx={{ flex: 1, fontSize: 13, color: MUTED }}>{lines.filter((line) => line.qty > 0).length} line{lines.filter((line) => line.qty > 0).length === 1 ? '' : 's'} · {vendorGroups.filter((group) => group.lines.some((line) => line.qty > 0)).length} PO{vendorGroups.filter((group) => group.lines.some((line) => line.qty > 0)).length === 1 ? '' : 's'} · {totalAmount ? money(totalAmount) : 'no rates'}</Typography>
        <PrimaryButton disabled={saving || !lines.some((line) => line.qty > 0)} onClick={place}>{saving ? 'Placing…' : 'Place order'}</PrimaryButton>
      </ScreenFooter>
    </>;
  } else {
    content = <>
      <ScreenBar title="Orders placed" />
      <Stack alignItems="center" justifyContent="center" gap={1.25} sx={{ flex: 1, p: 3, textAlign: 'center' }}>
        <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#e5f2ea', color: GREEN, display: 'grid', placeItems: 'center', fontSize: 30 }}>✓</Box>
        <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 21 }}>{placed.length} order{placed.length === 1 ? '' : 's'} placed</Typography>
        {placed.map((entry) => <Typography key={entry.po} sx={{ color: MUTED, fontSize: 13 }}>{entry.vendor} · {entry.po}{entry.date ? ` · deliver by ${fmtDate(entry.date)}` : ''}</Typography>)}
        <Typography sx={{ color: MUTED, fontSize: 13 }}>They now show under Orders, and site staff will see them as expected deliveries.</Typography>
        <OutlineButton onClick={() => { setPlaced([]); setStep('home'); }} sx={{ mt: 1 }}>Back to orders</OutlineButton>
      </Stack>
    </>;
  }
    return content;
  };

  // On a phone the outer card border just eats width; it earns its place only on a wider window.
  const frame = isDesktop
    ? { minHeight: 560, display: 'flex', flexDirection: 'column', border: `1px solid ${BORDER_STRONG}`, borderRadius: '15px', bgcolor: '#fff', overflow: 'hidden' } as const
    : { minHeight: 560, display: 'flex', flexDirection: 'column', bgcolor: '#fff', overflow: 'hidden' } as const;
  const newMaterialDialog = onCreateMaterial ? <NewMaterialDialog open={newMaterialOpen} initialName={needle ? typed : ''} items={materialItems} onClose={() => setNewMaterialOpen(false)} onCreate={createdMaterial} onAddVariety={addedVariety} onUse={useExisting} /> : null;
  return <Box sx={{ maxWidth: { xs: 440, md: 620 }, mx: 'auto', width: '100%', ...frame }}>{renderScreen(step, false)}{newMaterialDialog}</Box>;
}

const segSx = { '& .MuiToggleButton-root': { minHeight: 46, textTransform: 'none', fontWeight: 600, borderRadius: '15px', '&.Mui-selected': { bgcolor: GOLD_WASH, color: GOLD_D, border: `2px solid ${GOLD}` } } };
